// Per-design bg colors. The Social Post route generates the product image
// against this solid color (server-side Pillow) so the cleaned product
// composites seamlessly into the design's product slot.
//
// To add a new design: register its `SocialDesignId` here with the same
// hex value used in its .css `background:` rule. The template_id the
// frontend sends to /api/generate is `social-bg-<id>` — backend detects
// the prefix and uses these hex values verbatim.

import type { SocialDesignId } from '../StepSocialDesign'

export const SOCIAL_DESIGN_BG_COLOR: Record<SocialDesignId, string> = {
  // Each entry must match the product SLOT background (not the post bg), so
  // the AI-composed product image blends seamlessly into the slot rather
  // than showing a colored rectangle inside it.
  editorial: '#efe7d8',  // .post-editorial cream — slot is transparent, post bg shows through
  blush:     '#f9dccb',  // .post-blush .product-blob blob fill
  botanical: '#3a4639',  // .post-botanical .right dark green frame
  mono:      '#1d1b17',  // .post-mono center of the product slot's radial gradient
  noir:      '#e8e3d8',  // .post-noir .product warm-stone slot
  pop:       '#0a0a0a',  // .post-pop .slot rounded black square inside the pink blob
  vintage:   '#dfcfb3',  // .post-vintage .right beige frame
  street:    '#1a1814',  // .post-street .product dark coffee frame
}

/** Output aspect for the product slot. Editorial's slot is roughly 5:7
 *  portrait, so '2:3' (OpenAI-native 1024×1536) gives a tight fit
 *  without center-cropping. Tune per design as new ones ship. */
export const SOCIAL_DESIGN_SLOT_ASPECT: Record<SocialDesignId, '1:1' | '2:3'> = {
  editorial: '2:3',
  blush:     '1:1',      // .product-wrap is aspect-ratio: 1/1
  botanical: '2:3',      // .right column is tall portrait
  mono:      '2:3',      // .col-r product area is tall portrait
  noir:      '2:3',      // .no .right column is tall portrait
  pop:       '1:1',      // .pe .product-frame is aspect-ratio: 1/1
  vintage:   '2:3',      // .vt .right column is tall portrait
  street:    '2:3',      // .sw .product column is tall portrait
}

/** Dominant POST background color (the canvas itself, not the product
 *  slot). Used by the shimmer overlay's base tint and by html-to-image's
 *  backgroundColor so the exported PNG has matching edges. For multi-
 *  layer gradient backgrounds we pick the base color. */
export const SOCIAL_DESIGN_POST_BG_COLOR: Record<SocialDesignId, string> = {
  editorial: '#efe7d8',
  blush:     '#fbeee2',
  botanical: '#2f3a30',
  mono:      '#0f0e0c',
  noir:      '#f4f1ec',  // .post-noir canvas
  pop:       '#ffea3a',  // .post-pop saturated yellow
  vintage:   '#e8d9c0',  // .post-vintage warm cream base
  street:    '#0d0d0d',  // .post-street near-black grid canvas
}
